import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SharedHeader } from "@/components/SharedHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Phone, Calendar, CreditCard, Users, ShoppingBag, Building2, Stethoscope, Car, Home, Scale, Utensils } from "lucide-react";

// Import case study images
import healthcareImg from "@/assets/case-studies/healthcare-clinic.jpg";
import ecommerceImg from "@/assets/case-studies/ecommerce-shopify.jpg";
import hotelImg from "@/assets/case-studies/hotel-hospitality.jpg";
import realEstateImg from "@/assets/case-studies/real-estate.jpg";
import insuranceImg from "@/assets/case-studies/insurance-finance.jpg";
import restaurantImg from "@/assets/case-studies/restaurant.jpg";
import lawFirmImg from "@/assets/case-studies/law-firm.jpg";
import saasTechImg from "@/assets/case-studies/saas-tech.jpg";
import autoDealershipImg from "@/assets/case-studies/auto-dealership.jpg";
import homeServicesImg from "@/assets/case-studies/home-services.jpg";

interface CaseStudy {
  id: string;
  title: string;
  company: string;
  industry: string;
  callType: "inbound" | "outbound" | "both";
  integrations: string[];
  heroImage: string;
  icon: React.ReactNode;
  results: {
    metric: string;
    value: string;
  }[];
  summary: string;
  gradient: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: "healthcare-clinic",
    title: "How a Multi-Location Medical Clinic Reduced No-Shows by 67% with AI Phone Agents",
    company: "Regional Family Health Network",
    industry: "Healthcare",
    callType: "inbound",
    integrations: ["Calendar", "EHR System", "HIPAA Compliant"],
    heroImage: healthcareImg,
    icon: <Stethoscope className="h-5 w-5" />,
    results: [
      { metric: "No-Show Reduction", value: "67%" },
      { metric: "Calls Handled/Day", value: "340+" },
      { metric: "Patient Satisfaction", value: "4.9/5" },
    ],
    summary: "A 12-location medical clinic network automated appointment scheduling and reminders, handling 340+ daily calls while maintaining HIPAA compliance.",
    gradient: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "ecommerce-shopify",
    title: "Online Fashion Retailer Increased Order Recovery by 45% Using AI Outbound Calls",
    company: "Leading Fashion E-Commerce Brand",
    industry: "E-Commerce",
    callType: "outbound",
    integrations: ["Shopify", "Payment Gateway", "CRM"],
    heroImage: ecommerceImg,
    icon: <ShoppingBag className="h-5 w-5" />,
    results: [
      { metric: "Cart Recovery", value: "45%" },
      { metric: "Revenue Increase", value: "$127K/mo" },
      { metric: "Response Rate", value: "78%" },
    ],
    summary: "An online fashion retailer deployed AI agents to recover abandoned carts and upsell products, generating an additional $127K monthly revenue.",
    gradient: "from-purple-500/20 to-pink-500/20",
  },
  {
    id: "hotel-hospitality",
    title: "Luxury Hotel Chain: 24/7 AI Concierge Serving 15,000 Guests Monthly",
    company: "Premium Hospitality Group",
    industry: "Hospitality",
    callType: "inbound",
    integrations: ["Hotel Reservation System", "Calendar", "CRM"],
    heroImage: hotelImg,
    icon: <Building2 className="h-5 w-5" />,
    results: [
      { metric: "Booking Increase", value: "38%" },
      { metric: "Wait Time", value: "<10 sec" },
      { metric: "Guest Satisfaction", value: "94%" },
    ],
    summary: "A luxury hotel chain implemented AI phone agents for reservations, room service requests, and concierge services across 8 properties.",
    gradient: "from-amber-500/20 to-orange-500/20",
  },
  {
    id: "real-estate",
    title: "Real Estate Brokerage Qualified 3x More Leads with AI-Powered Outreach",
    company: "Top-Performing Realty Group",
    industry: "Real Estate",
    callType: "outbound",
    integrations: ["Salesforce CRM", "Calendar", "Lead Scoring"],
    heroImage: realEstateImg,
    icon: <Home className="h-5 w-5" />,
    results: [
      { metric: "Lead Qualification", value: "3x" },
      { metric: "Agent Time Saved", value: "25 hrs/wk" },
      { metric: "Deal Closure Rate", value: "+34%" },
    ],
    summary: "A real estate brokerage automated lead qualification and follow-ups, allowing agents to focus on high-intent buyers and close 34% more deals.",
    gradient: "from-blue-500/20 to-indigo-500/20",
  },
  {
    id: "insurance-finance",
    title: "Regional Insurance Provider Collects $2.3M More in Premiums Annually",
    company: "Established Insurance Company",
    industry: "Insurance & Finance",
    callType: "outbound",
    integrations: ["Payment Gateway", "CRM", "Policy Management"],
    heroImage: insuranceImg,
    icon: <CreditCard className="h-5 w-5" />,
    results: [
      { metric: "Collection Rate", value: "+52%" },
      { metric: "Annual Recovery", value: "$2.3M" },
      { metric: "Customer Retention", value: "89%" },
    ],
    summary: "An insurance provider automated premium collection calls and policy renewals, recovering $2.3M in overdue payments within the first year.",
    gradient: "from-cyan-500/20 to-blue-500/20",
  },
  {
    id: "restaurant-group",
    title: "Fine Dining Restaurant Group: 500+ Daily Reservations, Zero Wait",
    company: "Award-Winning Restaurant Group",
    industry: "Restaurant",
    callType: "inbound",
    integrations: ["Reservation System", "Payment Gateway", "POS"],
    heroImage: restaurantImg,
    icon: <Utensils className="h-5 w-5" />,
    results: [
      { metric: "Reservations/Day", value: "500+" },
      { metric: "Table Utilization", value: "+28%" },
      { metric: "Staff Hours Saved", value: "60 hrs/wk" },
    ],
    summary: "A restaurant group with 6 locations automated phone reservations and takeout orders, increasing table utilization by 28% during peak hours.",
    gradient: "from-red-500/20 to-orange-500/20",
  },
  {
    id: "law-firm",
    title: "Mid-Sized Law Firm: Never Miss a Client Consultation Again",
    company: "Growing Legal Practice",
    industry: "Legal Services",
    callType: "both",
    integrations: ["Calendar", "CRM", "Client Portal"],
    heroImage: lawFirmImg,
    icon: <Scale className="h-5 w-5" />,
    results: [
      { metric: "New Client Intake", value: "+45%" },
      { metric: "After-Hours Capture", value: "92%" },
      { metric: "Consultation Bookings", value: "2.1x" },
    ],
    summary: "A mid-sized law firm implemented 24/7 AI reception to capture after-hours leads and schedule consultations, growing new client intake by 45%.",
    gradient: "from-slate-500/20 to-zinc-500/20",
  },
  {
    id: "saas-tech",
    title: "B2B SaaS Company Cut Support Costs by 62% with AI Call Center",
    company: "Fast-Growing Software Company",
    industry: "SaaS / Technology",
    callType: "inbound",
    integrations: ["Zendesk", "Salesforce", "Slack"],
    heroImage: saasTechImg,
    icon: <Users className="h-5 w-5" />,
    results: [
      { metric: "Support Cost Reduction", value: "62%" },
      { metric: "First Call Resolution", value: "84%" },
      { metric: "CSAT Score", value: "4.7/5" },
    ],
    summary: "A B2B SaaS company deployed AI agents for tier-1 support, resolving 84% of issues on first contact while cutting support costs by 62%.",
    gradient: "from-violet-500/20 to-purple-500/20",
  },
  {
    id: "auto-dealership",
    title: "Multi-Brand Dealership Booked 127% More Test Drives with AI",
    company: "Regional Auto Dealer Group",
    industry: "Automotive",
    callType: "outbound",
    integrations: ["DealerCRM", "Calendar", "Inventory System"],
    heroImage: autoDealershipImg,
    icon: <Car className="h-5 w-5" />,
    results: [
      { metric: "Test Drive Bookings", value: "+127%" },
      { metric: "Lead Response Time", value: "<2 min" },
      { metric: "Sales Conversion", value: "+41%" },
    ],
    summary: "A multi-brand dealership automated lead follow-ups and service reminders, increasing test drive bookings by 127% and sales by 41%.",
    gradient: "from-zinc-500/20 to-neutral-500/20",
  },
  {
    id: "home-services",
    title: "HVAC Company: 24/7 Emergency Dispatch Without the Night Shift",
    company: "Local Home Services Provider",
    industry: "Home Services",
    callType: "inbound",
    integrations: ["ServiceTitan", "Calendar", "Dispatch System"],
    heroImage: homeServicesImg,
    icon: <Phone className="h-5 w-5" />,
    results: [
      { metric: "After-Hours Jobs", value: "+89%" },
      { metric: "Response Time", value: "<30 sec" },
      { metric: "Revenue Growth", value: "$340K/yr" },
    ],
    summary: "An HVAC company implemented AI answering service for emergency calls and scheduling, capturing $340K in additional annual revenue from after-hours jobs.",
    gradient: "from-orange-500/20 to-yellow-500/20",
  },
];

export default function CaseStudies() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <SharedHeader showNav={false} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge variant="outline" className="mb-4 px-4 py-1.5 text-sm font-medium border-primary/30 bg-primary/5">
            Real Results from Real Businesses
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="text-primary">Case Studies</span>
            <br />
            <span className="text-foreground">See How Companies Transform with AI</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Discover how businesses across 10 industries are using AI phone agents to reduce costs, 
            increase revenue, and deliver exceptional customer experiences 24/7.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
            {[
              { value: "10+", label: "Industries" },
              { value: "$5M+", label: "Revenue Generated" },
              { value: "500K+", label: "Calls Handled" },
              { value: "4.8/5", label: "Avg. Satisfaction" },
            ].map((stat, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-4">
                <div className="text-2xl md:text-3xl font-bold text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid gap-8">
            {caseStudies.map((study, index) => (
              <Link 
                key={study.id} 
                to={`/case-studies/${study.id}`}
                className="group"
              >
                <Card className={`overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-xl bg-gradient-to-br ${study.gradient}`}>
                  <CardContent className="p-0">
                    <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                      {/* Image */}
                      <div className="lg:w-1/2 relative overflow-hidden">
                        <img 
                          src={study.heroImage} 
                          alt={study.title}
                          className="w-full h-64 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                          <Badge className="bg-background/90 text-foreground">
                            {study.icon}
                            <span className="ml-1.5">{study.industry}</span>
                          </Badge>
                          <Badge variant={study.callType === "inbound" ? "default" : study.callType === "outbound" ? "secondary" : "outline"}>
                            {study.callType === "both" ? "Inbound + Outbound" : study.callType.charAt(0).toUpperCase() + study.callType.slice(1)}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="lg:w-1/2 p-6 lg:p-8 flex flex-col justify-center">
                        <p className="text-sm text-primary font-medium mb-2">{study.company}</p>
                        <h2 className="text-xl lg:text-2xl font-bold mb-4 group-hover:text-primary transition-colors">
                          {study.title}
                        </h2>
                        <p className="text-muted-foreground mb-6">{study.summary}</p>
                        
                        {/* Results */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          {study.results.map((result, idx) => (
                            <div key={idx} className="text-center">
                              <div className="text-lg lg:text-xl font-bold text-primary">{result.value}</div>
                              <div className="text-xs text-muted-foreground">{result.metric}</div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Integrations */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {study.integrations.map((integration, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {integration}
                            </Badge>
                          ))}
                        </div>
                        
                        {/* CTA */}
                        <div className="flex items-center text-primary font-medium group-hover:gap-3 transition-all">
                          Read Full Case Study
                          <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-primary/5">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join these industry leaders and transform your phone operations with AI. 
            Get started in minutes, not months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/chat-to-agent">
              <button className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 transition-opacity">
                Build Your AI Agent Free
              </button>
            </Link>
            <Link to="/">
              <button className="px-8 py-4 border border-border rounded-xl font-semibold hover:bg-muted transition-colors">
                Explore Features
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © 2025 Call24x7. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
